import {useFeatureFlags} from '@smart/feature-app';
import {
  Accordion,
  Checkbox,
  ColorTile,
  Heading,
  IconButton,
  Radio,
  getHexFromColorCode,
} from '@smart/web-components';
import classNames from 'classnames';
import * as React from 'react';
import {UsedFeatureFlags} from '../../constants/common';
import {useAppModel, useProductSearch} from '../../contexts';
import styles from './index.module.scss';
export type HandleOptionChange = (
  option: FacetOption,
  selected: boolean,
  skipSyncQuery?: boolean,
) => void;

export type FacetOption = {
  facetId: string;
  id: string;
  displayName: string;
  selected: boolean;
  value: string;
  disabled?: boolean;
  imageUrl?: string;
  priority?: number;
};

export type OptionType = 'checkbox' | 'exterior-color' | 'interior-color';

export type Facet = {
  id: string;
  displayName: string;
  displayOptionType: OptionType;
  isOpen: boolean;
  options: FacetOption[];
};

export type FacetPanelProps = {
  closeFacet: () => void;
  tagElement: React.ReactNode;
};

const renderColorTile = (facetOption: FacetOption) => {
  const [primary, secondary] = facetOption.id.split(`/`);
  const primaryColorHex = getHexFromColorCode(primary);
  const secondaryColorHex = getHexFromColorCode(secondary, ``);

  if (!primary && !secondary) return null;
  return (
    <ColorTile
      id={facetOption.id}
      primaryColor={primaryColorHex}
      secondaryColor={secondaryColorHex}
      disabled={facetOption.disabled}
      selected={facetOption.selected}
      titleText={facetOption.displayName}
    />
  );
};

const renderOption = (
  option: FacetOption,
  displayOptionType: string,
  handleOptionChange: HandleOptionChange,
  isLoading: boolean,
) => {
  const handleChange = (event: React.KeyboardEvent<HTMLLIElement>) => {
    // Spacebar
    if (event.key === ` `) {
      handleOptionChange(option, !option.selected);
    }
  };

  if (displayOptionType === `checkbox`) {
    return (
      <li
        tabIndex={0}
        key={`facet-${option.facetId}-option-${option.id}-${option.selected}`}
      >
        <Checkbox
          label={option.displayName}
          id={option.value}
          value={option.facetId === `line` ? option.displayName : option.value}
          checked={option.selected}
          disabled={option.disabled || isLoading}
          onChange={() => handleOptionChange(option, !option.selected)}
        />
      </li>
    );
  }

  if (displayOptionType === `radiobutton`) {
    return (
      <li
        key={`facet-${option.facetId}-option-${option.id}-${option.selected}`}
      >
        <Radio
          label={option.displayName}
          id={option.value}
          value={option.value}
          disabled={option.disabled}
          onChange={() => handleOptionChange(option, !option.selected)}
        />
      </li>
    );
  }

  const isColor =
    displayOptionType === `exterior-color` ||
    displayOptionType === `interior-color`;

  if (isColor && option.imageUrl) {
    return (
      <li
        onKeyUp={(event) => handleChange(event)}
        tabIndex={0}
        key={`facet-${option.facetId}-option-${option.id}-${option.selected}`}
        onClick={() => {
          if (option.disabled) {
            return;
          }
          handleOptionChange(option, !option.selected);
        }}
      >
        {renderColorTile(option)}
      </li>
    );
  }

  return null;
};

export const FacetPanel = ({closeFacet, tagElement}: FacetPanelProps) => {
  const {facets, activeFilters, toggleFilter, toggleFilterOption, loading} =
    useProductSearch();
  const {appModel} = useAppModel();
  const {enabled: enableFilters} = useFeatureFlags(
    UsedFeatureFlags.ENABLE_STOCK_SEARCH_FILTER_V2,
  );
  const headingContainer = classNames(styles.headingContainer, {
    [`${styles.headingContainerEnable}`]: enableFilters,
  });
  const paddingTop = classNames(styles.paddingTop, {
    [`${styles.paddingTopEnable}`]: enableFilters,
  });
  const facetPanelOptionList = classNames(styles.facetPanelOptionList, {
    [`${styles.facetPanelOptionListEnable}`]: enableFilters,
  });
  const renderTags = activeFilters.length > 0 && enableFilters;

  return (
    <>
      <div className={headingContainer}>
        <Heading variant={`headline-functional-400`}>
          {appModel?.showFiltersButtonText}
        </Heading>
        <IconButton
          variant="ghost"
          iconName="close-without-circle--small"
          size="200"
          ariaLabel="close"
          onClick={closeFacet}
        />
      </div>
      {renderTags && <div className={styles.tagsContainer}>{tagElement}</div>}
      {facets.map((facet) => (
        <Accordion
          className={paddingTop}
          key={`facet-panel-${facet.id}`}
          items={[
            {
              headline: {
                text: facet.displayName,
                tag: `h1`,
                variant: `headline-functional-50`,
              },
              isOpen: facet.isOpen,
              content: (
                <ul
                  tabIndex={0}
                  className={`${facetPanelOptionList} ${
                    (facet.displayOptionType === `exterior-color` ||
                      facet.displayOptionType === `interior-color`) &&
                    styles.facetPanelOptionListColors
                  }`}
                >
                  {facet.options.map((option) => {
                    const onOptionChange = () => {
                      toggleFilterOption({
                        facetId: option.facetId,
                        id: option.id,
                        selected: !option.selected,
                      });
                    };

                    return renderOption(
                      option,
                      facet.displayOptionType,
                      onOptionChange,
                      loading,
                    );
                  })}
                </ul>
              ),
              onClick: () => toggleFilter(facet.id),
            },
          ]}
        />
      ))}
    </>
  );
};
